import CardAIStream from "@/components/card-ai-stream";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { useModalOverlay } from "@/stores/modal-overlay-store";
import { XIcon } from "lucide-react";
import { createPortal } from "react-dom";

type NewsModalProps = {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Null when the only count known is a bucket: no badge. */
  badgeCount: number | null;
  streamUrl: string;
  launchFetch: boolean;
  hasAiSummary: boolean;
  onClose: () => void;
  sourcesContent: React.ReactNode;
};

export function NewsModal({
  title,
  icon: Icon,
  badgeCount,
  streamUrl,
  launchFetch,
  hasAiSummary,
  onClose,
  sourcesContent,
}: NewsModalProps): React.ReactPortal {
  const { t } = useTranslation();
  // Hand-rolled backdrop, hence a hand-rolled registration: the support bubble
  // renders above every overlay and only hides when told one is up.
  useModalOverlay(true);

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/80 animate-in fade-in duration-150"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
        <div
          className="pointer-events-auto w-[92vw] max-w-[1250px] h-[80vh] rounded-xl border bg-background shadow-2xl flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
            <div className="flex items-center gap-3">
              <div className="shrink-0 rounded-full flex items-center justify-center w-10 h-10 bg-muted">
                <Icon className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-semibold">{title}</h2>
              {badgeCount !== null && (
                <Badge variant="secondary">{badgeCount}</Badge>
              )}
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <XIcon className="w-5 h-5" />
            </Button>
          </div>

          {/* Body: two-pane or single */}
          <div className="flex flex-1 min-h-0">
            {hasAiSummary && (
              <div className="w-3/5 border-r overflow-y-auto p-6 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                <CardAIStream
                  title={title}
                  streamUrl={streamUrl}
                  launchFetch={launchFetch}
                  showAvatar={false}
                />
              </div>
            )}

            {/* Right pane: sources */}
            <div
              className={cn(
                "overflow-y-auto p-6 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]",
                hasAiSummary ? "w-2/5" : "w-full",
              )}
            >
              <h3 className="text-lg font-semibold mb-4">
                {t("company.news.sources")}
              </h3>
              {sourcesContent}
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
